<template>
  <v-container
    fluid
    class="pa-4 pa-md-6">
    <!-- Header -->
    <div class="d-flex align-center mb-5">
      <v-icon
        color="primary"
        size="28"
        class="mr-3">
        mdi-book-open-page-variant-outline
      </v-icon>
      <div>
        <h1 class="text-h5 font-weight-bold mb-0">
          คู่มือการใช้งาน Delivery Booking
        </h1>
        <p class="text-caption grey--text mb-0">
          ระบบจัดการการจองส่งสินค้าเข้าคลัง
        </p>
      </div>
    </div>

    <!-- Tabs -->
    <v-card
      outlined
      class="mb-0"
      style="border-bottom: none; border-radius: 8px 8px 0 0;">
      <v-tabs
        v-model="activeTab"
        color="primary"
        background-color="transparent"
        show-arrows>
        <v-tab
          v-for="tab in visibleTabs"
          :key="tab.key"
          class="text-capitalize">
          <v-icon
            small
            left>
            {{ tab.icon }}
          </v-icon>
          <span class="text-body-2 font-weight-medium">{{ tab.title }}</span>
        </v-tab>
      </v-tabs>
    </v-card>

    <!-- Lightbox Dialog -->
    <v-dialog
      v-model="lightboxDialog"
      max-width="900"
      @click:outside="lightboxDialog = false">
      <v-card>
        <v-card-title class="pa-3 d-flex align-center justify-space-between">
          <span class="text-body-2 grey--text">{{ lightboxLabel }}</span>
          <v-btn
            icon
            small
            @click="lightboxDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <v-img
            :src="lightboxSrc"
            contain
            max-height="75vh"
            class="rounded" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-card
      outlined
      style="border-radius: 0 0 8px 8px; border-top: none;">
      <v-tabs-items v-model="activeTab">
        <!-- Tab: รายการจอง -->
        <v-tab-item v-if="visibleTabs.some((t) => t.key === 'receive')">
          <div class="pa-6">
            <p class="text-body-2 grey--text text--darken-1 mb-6">
              วิธีดูและจัดการรายการจองส่งสินค้าประจำวัน ตั้งแต่เปิดปฏิทินจนถึงปฏิเสธการจัดส่ง
            </p>
            <v-card
              v-for="(step, i) in visibleReceiveSteps"
              :key="i"
              outlined
              class="mb-4">
              <v-card-text class="pa-5">
                <div class="d-flex align-center mb-3">
                  <div class="receive-step-num mr-3 flex-shrink-0">
                    {{ i + 1 }}
                  </div>
                  <p class="text-subtitle-2 font-weight-bold mb-0">
                    {{ step.title }}
                  </p>
                </div>
                <v-row
                  align="start"
                  no-gutters>
                  <v-col>
                    <v-row>
                      <v-col
                        cols="12"
                        md="7"
                        class="pr-md-6">
                        <p class="text-caption primary--text mb-3">
                          {{ step.subtitle }}
                        </p>
                        <p class="text-body-2 grey--text text--darken-2 mb-3">
                          {{ step.desc }}
                        </p>
                        <div
                          class="text-body-2 pl-4 mb-3">
                          <p
                            v-for="(b, bulletNumber) in step.bullets"
                            :key="b"
                            class="mb-1 grey--text text--darken-2">
                            {{ i + 1 }}.{{ bulletNumber + 1 }} {{ b }}
                          </p>
                        </div>
                        <v-alert
                          v-if="step.note"
                          :type="step.noteType || 'info'"
                          text
                          dense
                          class="mb-0">
                          {{ step.note }}
                        </v-alert>
                      </v-col>
                      <v-col
                        cols="12"
                        md="5">
                        <v-img
                          v-if="step.img"
                          :src="step.img"
                          contain
                          max-height="320"
                          class="rounded img-clickable"
                          style="border: 1px solid #e0e0e0; min-height: 140px;"
                          @click="openLightbox(step.img, step.imgLabel)">
                          <template v-slot:placeholder>
                            <div class="img-placeholder">
                              <v-icon
                                size="28"
                                color="grey lighten-1">
                                mdi-image-outline
                              </v-icon>
                              <span class="text-caption grey--text mt-1">{{ step.imgLabel }}</span>
                            </div>
                          </template>
                        </v-img>
                        <div
                          v-else
                          class="img-placeholder">
                          <v-icon
                            size="28"
                            color="grey lighten-1">
                            mdi-image-outline
                          </v-icon>
                          <span class="text-caption grey--text mt-1">{{ step.imgLabel }}</span>
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>
        </v-tab-item>

        <!-- Tab: ตัวกรองการจอง -->
        <v-tab-item v-if="visibleTabs.some((t) => t.key === 'filter')">
          <div class="pa-6">
            <p class="text-body-2 grey--text text--darken-1 mb-6">
              ตัวกรองในหน้า Booking List ช่วยให้ค้นหาและแสดงเฉพาะรายการที่ต้องการได้รวดเร็ว
            </p>
            <v-row>
              <v-col
                v-for="filter in filterItems"
                :key="filter.key"
                cols="12"
                sm="6"
                md="4">
                <v-card
                  outlined
                  height="100%">
                  <v-card-text class="pa-4">
                    <div class="d-flex align-center mb-2">
                      <v-icon
                        :color="filter.color"
                        size="20"
                        class="mr-2">
                        {{ filter.icon }}
                      </v-icon>
                      <span class="text-subtitle-2 font-weight-bold">{{ filter.label }}</span>
                    </div>
                    <p class="text-body-2 grey--text text--darken-2 mb-2">
                      {{ filter.desc }}
                    </p>
                    <v-chip
                      x-small
                      :color="filter.color"
                      outlined
                      class="text-caption">
                      {{ filter.type }}
                    </v-chip>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <div class="mt-6">
              <v-img
                :src="imgFilter01"
                contain
                class="rounded img-clickable"
                style="border: 1px solid #e0e0e0; min-height: 140px;"
                @click="openLightbox(imgFilter01, 'แถบตัวกรองในหน้า Booking List')">
                <template v-slot:placeholder>
                  <div class="img-placeholder">
                    <v-icon
                      size="28"
                      color="grey lighten-1">
                      mdi-image-outline
                    </v-icon>
                    <span class="text-caption grey--text mt-1">แถบตัวกรองในหน้า Booking List</span>
                  </div>
                </template>
              </v-img>
            </div>
          </div>
        </v-tab-item>

        <!-- Tab: พิมพ์ GRN -->
        <v-tab-item v-if="visibleTabs.some((t) => t.key === 'grn')">
          <div class="pa-6">
            <p class="text-body-2 grey--text text--darken-1 mb-6">
              ใบรับสินค้า (Goods Receipt Note) สามารถพิมพ์ได้ 2 วิธีขึ้นอยู่กับความต้องการ
            </p>
            <v-row>
              <v-col
                cols="12"
                md="6">
                <p class="text-subtitle-2 font-weight-bold mb-3">
                  <v-icon
                    small
                    color="blue darken-1"
                    class="mr-1">
                    mdi-file-outline
                  </v-icon>
                  พิมพ์ทีละใบ
                </p>
                <p class="text-caption grey--text mb-4">
                  สำหรับพิมพ์ GRN ของ booking รายการเดียว
                </p>
                <v-card
                  v-for="(step, i) in grnSingleSteps"
                  :key="i"
                  outlined
                  class="mb-3">
                  <v-card-text class="pa-4">
                    <div class="d-flex align-center">
                      <div class="receive-step-num mr-3">
                        {{ i + 1 }}
                      </div>
                      <p class="text-body-2 mb-0">
                        {{ step }}
                      </p>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col
                cols="12"
                md="6">
                <p class="text-subtitle-2 font-weight-bold mb-3">
                  <v-icon
                    small
                    color="green darken-1"
                    class="mr-1">
                    mdi-file-multiple-outline
                  </v-icon>
                  พิมพ์ทั้งวัน
                </p>
                <p class="text-caption grey--text mb-4">
                  สำหรับพิมพ์ GRN ทุกใบของวันที่เลือกพร้อมกัน
                </p>
                <v-card
                  v-for="(step, i) in grnBatchSteps"
                  :key="i"
                  outlined
                  class="mb-3">
                  <v-card-text class="pa-4">
                    <div class="d-flex align-center">
                      <div class="receive-step-num mr-3">
                        {{ i + 1 }}
                      </div>
                      <p class="text-body-2 mb-0">
                        {{ step }}
                      </p>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <div class="mt-5">
              <p class="text-subtitle-2 font-weight-bold mb-3">
                ตัวอย่างการพิมพ์ใบ GRN
              </p>
              <v-row>
                <v-col
                  cols="12"
                  md="6">
                  <p class="text-caption grey--text mb-2">
                    <v-icon
                      x-small
                      color="blue darken-1"
                      class="mr-1">
                      mdi-file-outline
                    </v-icon>
                    พิมพ์ทีละใบ (จากหน้า Detail)
                  </p>
                  <v-img
                    :src="imgGrnPrint"
                    contain
                    max-height="360"
                    class="rounded img-clickable"
                    style="border: 1px solid #e0e0e0; min-height: 140px;"
                    @click="openLightbox(imgGrnPrint, 'ตัวอย่างพิมพ์ทีละใบ')">
                    <template v-slot:placeholder>
                      <div class="img-placeholder">
                        <v-icon
                          size="28"
                          color="grey lighten-1">
                          mdi-image-outline
                        </v-icon>
                        <span class="text-caption grey--text mt-1">ตัวอย่างพิมพ์ทีละใบ</span>
                      </div>
                    </template>
                  </v-img>
                </v-col>
                <v-col
                  cols="12"
                  md="6">
                  <p class="text-caption grey--text mb-2">
                    <v-icon
                      x-small
                      color="green darken-1"
                      class="mr-1">
                      mdi-file-multiple-outline
                    </v-icon>
                    พิมพ์ทั้งวัน
                  </p>
                  <v-img
                    :src="imgGrnPrintBatch"
                    contain
                    max-height="360"
                    class="rounded img-clickable"
                    style="border: 1px solid #e0e0e0; min-height: 140px;"
                    @click="openLightbox(imgGrnPrintBatch, 'ตัวอย่างพิมพ์ทั้งวัน')">
                    <template v-slot:placeholder>
                      <div class="img-placeholder">
                        <v-icon
                          size="28"
                          color="grey lighten-1">
                          mdi-image-outline
                        </v-icon>
                        <span class="text-caption grey--text mt-1">ตัวอย่างพิมพ์ทั้งวัน</span>
                      </div>
                    </template>
                  </v-img>
                </v-col>
              </v-row>
            </div>

            <v-divider class="my-7" />

            <p class="text-subtitle-2 font-weight-bold mb-1">
              ขั้นตอนรับสินค้า
            </p>
            <p class="text-body-2 grey--text text--darken-1 mb-5">
              เมื่อพิมพ์ GRN แล้ว ให้ดำเนินการตามขั้นตอนด้านล่างเมื่อสินค้ามาถึง
            </p>
            <v-card
              v-for="(step, i) in receiveFlowSteps"
              :key="i"
              outlined
              class="mb-4">
              <v-card-text class="pa-5">
                <div class="d-flex align-center mb-3">
                  <div class="receive-step-num mr-3 flex-shrink-0">
                    {{ i + 1 }}
                  </div>
                  <p class="text-subtitle-2 font-weight-bold mb-0">
                    {{ step.title }}
                  </p>
                </div>
                <v-row
                  align="start"
                  no-gutters>
                  <v-col>
                    <v-row>
                      <v-col
                        cols="12"
                        md="7"
                        class="pr-md-6">
                        <p class="text-caption primary--text mb-3">
                          {{ step.subtitle }}
                        </p>
                        <p class="text-body-2 grey--text text--darken-2 mb-3">
                          {{ step.desc }}
                        </p>
                        <v-alert
                          v-if="step.note"
                          :type="step.noteType || 'warning'"
                          text
                          dense
                          class="mb-0">
                          {{ step.note }}
                        </v-alert>
                      </v-col>
                      <v-col
                        cols="12"
                        md="5">
                        <v-img
                          v-if="step.img"
                          :src="step.img"
                          contain
                          max-height="320"
                          class="rounded img-clickable"
                          style="border: 1px solid #e0e0e0; min-height: 140px;"
                          @click="openLightbox(step.img, step.imgLabel)">
                          <template v-slot:placeholder>
                            <div class="img-placeholder">
                              <v-icon
                                size="28"
                                color="grey lighten-1">
                                mdi-image-outline
                              </v-icon>
                              <span class="text-caption grey--text mt-1">{{ step.imgLabel }}</span>
                            </div>
                          </template>
                        </v-img>
                        <div
                          v-else
                          class="img-placeholder">
                          <v-icon
                            size="28"
                            color="grey lighten-1">
                            mdi-image-outline
                          </v-icon>
                          <span class="text-caption grey--text mt-1">{{ step.imgLabel }}</span>
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>
        </v-tab-item>

        <!-- Tab: ตั้งค่า Slots -->
        <v-tab-item v-if="visibleTabs.some((t) => t.key === 'slots')">
          <div class="pa-6">
            <div class="d-flex align-center mb-6">
              <p class="text-body-2 grey--text text--darken-1 mb-0 mr-3">
                กำหนดช่วงเวลาที่คลังเปิดรับการจองส่งสินค้า
                เข้าถึงจากเมนู <strong>Delivery Booking › Booking Slots</strong>
              </p>
              <v-chip
                small
                color="warning"
                text-color="white"
                label
                class="flex-shrink-0">
                <v-icon
                  x-small
                  left>
                  mdi-lock-outline
                </v-icon>
                Warehouse Manager ขึ้นไป
              </v-chip>
            </div>
            <v-card
              v-for="(step, i) in slotSteps"
              :key="i"
              outlined
              class="mb-4">
              <v-card-text class="pa-5">
                <div class="d-flex align-center mb-3">
                  <div class="receive-step-num mr-3 flex-shrink-0">
                    {{ i + 1 }}
                  </div>
                  <p class="text-subtitle-2 font-weight-bold mb-0">
                    {{ step.title }}
                  </p>
                </div>
                <v-row
                  align="start"
                  no-gutters>
                  <v-col>
                    <v-row>
                      <v-col
                        cols="12"
                        md="7"
                        class="pr-md-6">
                        <p class="text-caption primary--text mb-3">
                          {{ step.subtitle }}
                        </p>
                        <p class="text-body-2 grey--text text--darken-2 mb-3">
                          {{ step.desc }}
                        </p>
                        <ul
                          v-if="step.bullets"
                          class="text-body-2 pl-4 mb-0">
                          <li
                            v-for="b in step.bullets"
                            :key="b"
                            class="mb-1 grey--text text--darken-2">
                            {{ b }}
                          </li>
                        </ul>
                      </v-col>
                      <v-col
                        cols="12"
                        md="5">
                        <v-img
                          v-if="step.img"
                          :src="step.img"
                          contain
                          max-height="320"
                          class="rounded img-clickable"
                          style="border: 1px solid #e0e0e0; min-height: 140px;"
                          @click="openLightbox(step.img, step.imgLabel)">
                          <template v-slot:placeholder>
                            <div class="img-placeholder">
                              <v-icon
                                size="28"
                                color="grey lighten-1">
                                mdi-image-outline
                              </v-icon>
                              <span class="text-caption grey--text mt-1">{{ step.imgLabel }}</span>
                            </div>
                          </template>
                        </v-img>
                        <div
                          v-else
                          class="img-placeholder">
                          <v-icon
                            size="28"
                            color="grey lighten-1">
                            mdi-image-outline
                          </v-icon>
                          <span class="text-caption grey--text mt-1">{{ step.imgLabel }}</span>
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>
        </v-tab-item>

        <!-- Tab: ตั้งค่าวันหยุด -->
        <v-tab-item v-if="visibleTabs.some((t) => t.key === 'holiday')">
          <div class="pa-6">
            <div class="d-flex align-center mb-6">
              <p class="text-body-2 grey--text text--darken-1 mb-0 mr-3">
                กำหนดวันที่คลังไม่รับการจองส่งสินค้า
                เข้าถึงจากเมนู <strong>Delivery Booking › Booking Holiday</strong>
              </p>
              <v-chip
                small
                color="warning"
                text-color="white"
                label
                class="flex-shrink-0">
                <v-icon
                  x-small
                  left>
                  mdi-lock-outline
                </v-icon>
                Warehouse Manager ขึ้นไป
              </v-chip>
            </div>
            <v-card
              v-for="(step, i) in holidaySteps"
              :key="i"
              outlined
              class="mb-4">
              <v-card-text class="pa-5">
                <div class="d-flex align-center mb-3">
                  <div class="receive-step-num mr-3 flex-shrink-0">
                    {{ i + 1 }}
                  </div>
                  <p class="text-subtitle-2 font-weight-bold mb-0">
                    {{ step.title }}
                  </p>
                </div>
                <v-row
                  align="start"
                  no-gutters>
                  <v-col>
                    <v-row>
                      <v-col
                        cols="12"
                        md="7"
                        class="pr-md-6">
                        <p class="text-caption primary--text mb-3">
                          {{ step.subtitle }}
                        </p>
                        <p class="text-body-2 grey--text text--darken-2 mb-3">
                          {{ step.desc }}
                        </p>
                        <ul
                          v-if="step.bullets"
                          class="text-body-2 pl-4 mb-3">
                          <li
                            v-for="b in step.bullets"
                            :key="b"
                            class="mb-1 grey--text text--darken-2">
                            {{ b }}
                          </li>
                        </ul>
                        <v-alert
                          v-if="step.note"
                          type="info"
                          text
                          dense
                          class="mb-0">
                          {{ step.note }}
                        </v-alert>
                      </v-col>
                      <v-col
                        cols="12"
                        md="5">
                        <v-img
                          v-if="step.img"
                          :src="step.img"
                          contain
                          max-height="320"
                          class="rounded img-clickable"
                          style="border: 1px solid #e0e0e0; min-height: 140px;"
                          @click="openLightbox(step.img, step.imgLabel)">
                          <template v-slot:placeholder>
                            <div class="img-placeholder">
                              <v-icon
                                size="28"
                                color="grey lighten-1">
                                mdi-image-outline
                              </v-icon>
                              <span class="text-caption grey--text mt-1">{{ step.imgLabel }}</span>
                            </div>
                          </template>
                        </v-img>
                        <div
                          v-else
                          class="img-placeholder">
                          <v-icon
                            size="28"
                            color="grey lighten-1">
                            mdi-image-outline
                          </v-icon>
                          <span class="text-caption grey--text mt-1">{{ step.imgLabel }}</span>
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>
        </v-tab-item>

        <!-- Tab: สถานะการจอง -->
        <v-tab-item v-if="visibleTabs.some((t) => t.key === 'status')">
          <div class="pa-6">
            <p class="text-body-2 grey--text text--darken-1 mb-6">
              แต่ละการจองจะแสดงสถานะในตารางรายการและหน้ารายละเอียด สีและความหมายของแต่ละสถานะมีดังนี้
            </p>

            <v-row class="mb-8">
              <v-col
                v-for="status in statuses"
                :key="status.key"
                cols="12"
                sm="6"
                md="4">
                <v-card
                  outlined
                  height="100%">
                  <div
                    class="status-card-top pa-3 d-flex align-center"
                    :style="{ backgroundColor: status.bg }">
                    <span
                      class="text-subtitle-2 font-weight-bold"
                      :style="{ color: status.text }">
                      {{ status.label }}
                    </span>
                  </div>
                  <v-card-text class="pt-3">
                    <p class="text-body-2 font-weight-medium mb-1">
                      {{ status.meaning }}
                    </p>
                    <p class="text-caption grey--text mb-3">
                      {{ status.desc }}
                    </p>
                    <div v-if="status.actions && status.actions.length">
                      <p class="text-caption grey--text mb-1">
                        ดำเนินการได้:
                      </p>
                      <div class="d-flex flex-wrap">
                        <v-chip
                          v-for="a in status.actions"
                          :key="a"
                          x-small
                          outlined
                          class="mr-1 mb-1">
                          {{ a }}
                        </v-chip>
                      </div>
                    </div>
                    <p
                      v-else
                      class="text-caption grey--text mb-0">
                      ไม่สามารถดำเนินการเพิ่มเติมได้
                    </p>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-divider class="mb-6" />

            <p class="text-subtitle-2 font-weight-bold mb-3">
              สิทธิ์การดำเนินการตาม Role
            </p>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th class="text-left">
                    Role
                  </th>
                  <th class="text-center">
                    ดูรายการ
                  </th>
                  <th class="text-center">
                    ปฏิเสธ
                  </th>
                  <th class="text-center">
                    ตั้งค่า Slots / Holiday
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="role in roles"
                  :key="role.name">
                  <td>
                    <span class="text-caption">
                      {{ role.name.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') }}
                    </span>
                  </td>
                  <td class="text-center">
                    <v-icon
                      :color="role.view ? 'success' : 'grey lighten-1'"
                      small>
                      {{ role.view ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}
                    </v-icon>
                  </td>
                  <td class="text-center">
                    <v-icon
                      :color="role.reject ? 'success' : 'grey lighten-1'"
                      small>
                      {{ role.reject ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}
                    </v-icon>
                  </td>
                  <td class="text-center">
                    <v-icon
                      :color="role.settings ? 'success' : 'grey lighten-1'"
                      small>
                      {{ role.settings ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}
                    </v-icon>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-container>
</template>

<script>
import { getRole } from '@/assets/js/Authentication'

import imgFilter01 from '@/assets/image/manual/manual-filter-01-filter-panel.png'
import imgGrnPrint from '@/assets/image/manual/manual-grn-00-print-example.png'
import imgGrnPrintBatch from '@/assets/image/manual/manual-grn-00-print-batch.png'
import imgReceive01 from '@/assets/image/manual/manual-receive-01-booking-list.png'
import imgReceive02 from '@/assets/image/manual/manual-receive-02-select-date.png'
import imgReceive03 from '@/assets/image/manual/manual-receive-03-booking-detail.png'
import imgReceive04 from '@/assets/image/manual/manual-receive-04-reject-dialog.png'
import imgGrn01 from '@/assets/image/manual/manual-grn-01-qr-code.png'
import imgGrn02 from '@/assets/image/manual/manual-grn-02-scan-qr.png'
import imgGrn03Confirm from '@/assets/image/manual/manual-grn-03-confirm-button.png'
import imgGrn04Status from '@/assets/image/manual/manual-grn-04-status-changed.png'
import imgGrn05Check from '@/assets/image/manual/manual-grn-05-check-status.png'
import imgSlots01 from '@/assets/image/manual/manual-slots-01-slots-page.png'
import imgSlots02 from '@/assets/image/manual/manual-slots-02-add-slot.png'
import imgSlots03 from '@/assets/image/manual/manual-slots-03-edit-slot.png'
import imgHoliday01 from '@/assets/image/manual/manual-holiday-01-calendar.png'
import imgHoliday02 from '@/assets/image/manual/manual-holiday-02-add-holiday.png'
import imgHoliday02b from '@/assets/image/manual/manual-holiday-02b-add-slot-holiday.png'
import imgHoliday03 from '@/assets/image/manual/manual-holiday-03-delete-holiday.png'

const ADMIN_ROLES = ['management', 'developer', 'warehouse_manager']

export default {
  name: 'DeliveryBookingManual',
  data () {
    return {
      lightboxDialog: false,
      lightboxSrc: '',
      lightboxLabel: '',
      imgFilter01,
      imgGrnPrint,
      imgGrnPrintBatch,
      activeTab: 0,
      tabs: [
        { key: 'receive', title: 'รายการจอง', icon: 'mdi-clipboard-list-outline' },
        { key: 'filter', title: 'ตัวกรองการจอง', icon: 'mdi-filter-outline' },
        { key: 'grn', title: 'พิมพ์ GRN และรับสินค้า', icon: 'mdi-printer' },
        { key: 'slots', title: 'ตั้งค่า Slots', icon: 'mdi-clock-time-four-outline', adminOnly: true },
        { key: 'holiday', title: 'ตั้งค่าวันหยุด', icon: 'mdi-calendar-remove', adminOnly: true },
        { key: 'status', title: 'สถานะการจอง', icon: 'mdi-tag-outline' }
      ],
      receiveSteps: [
        {
          title: 'เปิดหน้า Booking List',
          subtitle: 'Delivery Booking › Booking List',
          desc: 'เข้าสู่หน้าหลักของระบบ จะเห็นปฏิทินรายสัปดาห์แสดงจำนวนการจองของแต่ละวัน',
          bullets: [
            'วันที่มีสีเทา = วันหยุดที่กำหนดไว้',
            'ตัวเลขใต้วันที่ = จำนวนการจองของวันนั้น',
            'การ์ดด้านบน = จำนวนการจองของวันปัจจุบัน เมื่อคลิกจะกลับมาเลือกวันปัจจุบันให้',
            'เข้ามาครั้งแรกจะแสดงรายการจองของวันปัจจุบัน'
          ],
          img: imgReceive01,
          imgLabel: 'หน้า Booking List — ปฏิทินรายสัปดาห์'
        },
        {
          title: 'เลือกวันที่ต้องการ',
          subtitle: 'คลิกที่วันบนปฏิทิน',
          desc: 'คลิกที่วันที่ต้องการจัดการ ตารางด้านล่างจะแสดงรายการจองของวันนั้นทั้งหมด สามารถเลื่อนดูได้ทีละ 1 สัปดาห์',
          bullets: [
            'เลขที่การจอง (BK-XXXX-XX-XXXX)',
            'ชื่อ Vendor / โรงงาน',
            'ช่วงเวลาจัดส่ง',
            'Collections',
            'สถานะการจอง'
          ],
          img: imgReceive02,
          imgLabel: 'ตารางรายการจองของวันที่เลือก'
        },
        {
          title: 'คลิกเข้าดูรายละเอียด',
          subtitle: 'คลิกที่เลขที่การจอง',
          desc: 'คลิกที่เลขที่การจองเพื่อเข้าสู่หน้ารายละเอียด จะแสดงข้อมูลครบทุกส่วน',
          bullets: [
            'ข้อมูลการจัดส่ง: vendor, วันที่, ช่วงเวลา',
            'เอกสารแนบ PO',
            'รายการสินค้า: SKU, จำนวนที่สั่ง',
            'ข้อมูลยานพาหนะ และ บรรจุภัณฑ์'
          ],
          img: imgReceive03,
          imgLabel: 'หน้า Booking Detail'
        },
        {
          title: 'ปฏิเสธการการจอง',
          subtitle: 'กรณีที่ไม่สามารถรับสินค้าได้',
          adminOnly: true,
          desc: 'หากต้องการปฏิเสธการจอง ให้คลิกปุ่ม "ปฏิเสธ" ในหน้ารายละเอียด จากนั้นระบบจะเปิด dialog ให้กรอกเหตุผล',
          bullets: [
            'เลือกหรือกรอกเหตุผลการปฏิเสธ เช่น เอกสารไม่ครบ / สินค้าไม่ตรง PO / ไม่มีพื้นที่รับสินค้า',
            'กด "ยืนยัน" เพื่อบันทึก สถานะจะเปลี่ยนเป็น "ปฏิเสธโดยคลัง"',
            'ระบบจะแจ้งเตือนการปฏิเสธไปที่ vendor พร้อมเหตุผลที่ระบุ'
          ],
          note: 'Warehouse Manager เท่านั้นที่มีสิทธิ์ปฏิเสธการจอง',
          noteType: 'warning',
          img: imgReceive04,
          imgLabel: 'Dialog กรอกเหตุผลปฏิเสธ'
        }
      ],
      filterItems: [
        {
          key: 'search',
          label: 'ค้นหา Booking No',
          icon: 'mdi-magnify',
          color: 'primary',
          type: 'Text search',
          desc: 'พิมพ์เลขที่การจอง (BK-XXXX-XX-XXXX) เพื่อค้นหาตรงๆ'
        },
        {
          key: 'vendor',
          label: 'Vendor',
          icon: 'mdi-factory',
          color: 'blue-grey',
          type: 'Autocomplete',
          desc: 'เลือกโรงงาน/vendor ได้หลายรายการพร้อมกัน'
        },
        {
          key: 'collection',
          label: 'Collection',
          icon: 'mdi-hanger',
          color: 'purple',
          type: 'Autocomplete',
          desc: 'กรองรายการตาม collection ของสินค้าที่ส่งมา เลือกได้หลาย collection'
        },
        {
          key: 'itemNo',
          label: 'SKU Code',
          icon: 'mdi-barcode',
          color: 'teal',
          type: 'Text search',
          desc: 'ระบุ SKU code เพื่อค้นหาการจองที่มีสินค้านั้นอยู่'
        }
      ],
      receiveFlowSteps: [
        {
          title: 'พิมพ์ใบ GRN มารอไว้',
          subtitle: 'ก่อนถึงเวลา booking',
          desc: 'พิมพ์ใบ GRN ของการจองที่คาดว่าจะมาส่ง เตรียมไว้ที่จุดรับสินค้า QR code อยู่ด้านท้ายใบ',
          img: imgGrn01,
          imgLabel: 'QR code ท้ายใบ GRN'
        },
        {
          title: 'สแกน QR code',
          subtitle: 'ให้พนักงานขนส่งสแกน QR code ท้ายใบ GRN',
          desc: 'เมื่อสินค้ามาส่งตาม booking ที่จองไว้ ให้นำใบ GRN ให้พนักงานขนส่งสแกน QR code ด้านท้ายใบ',
          note: 'หากพนักงานขนส่งไม่ยอมสแกน ทางคลังจะต้องสแกน QR Code และยืนยันการจัดส่งเองภายในวันที่จัดส่ง',
          noteType: 'warning',
          img: imgGrn02,
          imgLabel: 'QR code ที่ต้องสแกน'
        },
        {
          title: 'กดยืนยันการจัดส่ง',
          subtitle: 'ยืนยันการจัดส่ง',
          desc: 'หลังสแกน QR code หน้าจอจะแสดงข้อมูลการจอง กดปุ่ม "ยืนยันการจัดส่ง" เพื่อยืนยันการรับสินค้า',
          img: imgGrn03Confirm,
          imgLabel: 'หน้าจอยืนยัน — ปุ่ม เสร็จสิ้น'
        },
        {
          title: 'สถานะในหน้า Confirm เปลี่ยน',
          subtitle: 'ระบบอัปเดตทันที',
          desc: 'หลังกด "ยืนยันการจัดส่ง" หน้าจอจะแสดงสถานะ "จัดส่งสำเร็จ" ให้พนักงานขนส่งเห็นทันที',
          img: imgGrn04Status,
          imgLabel: 'หน้าจอแสดงสถานะ จัดส่งสำเร็จ'
        },
        {
          title: 'ตรวจสอบสถานะในหน้า Booking List',
          subtitle: 'Delivery Booking › Booking List',
          desc: 'กลับมาที่หน้า Booking List สถานะของรายการจะเปลี่ยนจาก "รอจัดส่ง" เป็น "จัดส่งสำเร็จ" โดยอัตโนมัติ',
          img: imgGrn05Check,
          imgLabel: 'สถานะ จัดส่งสำเร็จ ในหน้า Booking List'
        }
      ],
      grnSingleSteps: [
        'เข้าหน้า Booking Detail ของรายการที่ต้องการ',
        'คลิกปุ่ม "พิมพ์ GRN" มุมบนขวา',
        'ระบบเปิดหน้าใบ GRN ในแท็บใหม่',
        'คลิกปุ่ม Print แล้วเลือกเครื่องพิมพ์'
      ],
      grnBatchSteps: [
        'เข้าหน้า Booking List',
        'คลิกวันที่ต้องการพิมพ์บนปฏิทิน',
        'คลิกปุ่ม "พิมพ์ใบนัดสินค้าทั้งหมดของวันนี้" มุมบนขวา',
        'ระบบเปิดหน้า GRN รวมทุกใบของวันนั้น',
        'คลิกปุ่ม Print แล้วเลือกเครื่องพิมพ์'
      ],
      slotSteps: [
        {
          title: 'เข้าหน้า Booking Slots',
          subtitle: 'Delivery Booking › Booking Slots',
          desc: 'หน้านี้แสดงรายการ time slot ทั้งหมดทางด้านซ้าย และฟอร์มเพิ่ม slot ใหม่ทางด้านขวา',
          img: imgSlots01,
          imgLabel: 'หน้า Booking Slots'
        },
        {
          title: 'เพิ่ม Slot ใหม่',
          subtitle: 'กรอกเวลาในฟอร์มด้านขวา',
          desc: 'กรอกเวลาเริ่มต้นและสิ้นสุด จากนั้นกด "เพิ่ม SLOT"',
          bullets: [
            'เวลาเริ่มต้น — รูปแบบ HH:mm เช่น 09:00',
            'เวลาสิ้นสุด — ต้องหลังเวลาเริ่มต้น เช่น 09:30',
            'จอง 2 slot — การที่จะจอง 2 slot ติดกันได้ต้องตั้งเวลาให้ต่อเนื่องกัน',
            'เวลาต่อเนื่องกัน — เวลาเริ่มต้องมีค่าเท่าเวลาสิ้นสุด slot ก่อนหน้า เช่น 13.00 - 14.00 | 14.00 - 15.00'
          ],
          img: imgSlots02,
          imgLabel: 'ฟอร์มเพิ่ม Slot'
        },
        {
          title: 'แก้ไขหรือลบ Slot',
          subtitle: 'จัดการ Slot ที่มีอยู่',
          desc: 'ในตารางรายการ Slot แต่ละแถวมีปุ่มดำเนินการ',
          bullets: [
            'ปุ่มดินสอ — แก้ไขเวลา',
            'ปุ่มถังขยะ — ลบ Slot (มี dialog ยืนยัน)'
          ],
          img: imgSlots03,
          imgLabel: 'ปุ่มแก้ไข / ลบ Slot'
        }
      ],
      holidaySteps: [
        {
          title: 'เข้าหน้า Booking Holiday',
          subtitle: 'Delivery Booking › Booking Holiday',
          desc: 'หน้านี้แสดงปฏิทินรายเดือน วันที่มีสีเน้น = วันหยุดที่กำหนดไว้แล้ว',
          bullets: [
            'วันที่มีแถบสีแดง = ปิดทั้งวัน (Full Day)',
            'วันที่มีแถบสีส้ม = ปิดบางช่วงเวลา (Custom)',
            'ปุ่ม "วันนี้" = กลับมาเดือนปัจจุบัน'
          ],
          img: imgHoliday01,
          imgLabel: 'หน้า Booking Holiday — ปฏิทิน'
        },
        {
          title: 'เพิ่มวันหยุด — ปิดทั้งวัน',
          subtitle: 'คลิกวันที่บนปฏิทิน → เลือก "ปิดทั้งวัน"',
          desc: 'คลิกวันที่ที่ต้องการ แผงด้านขวาจะเปิด เลือกประเภท "ปิดทั้งวัน" กรอกหมายเหตุ แล้วกด "บันทึกการตั้งค่า"',
          bullets: [
            'วันที่นั้นจะปิดรับ booking ทุก slot',
            'ปฏิทินจะแสดงแถบสีแดงบนวันนั้น'
          ],
          img: imgHoliday02,
          imgLabel: 'ฟอร์มเพิ่มวันหยุด — ปิดทั้งวัน'
        },
        {
          title: 'เพิ่มวันหยุด — ปิดบางสล็อต',
          subtitle: 'คลิกวันที่บนปฏิทิน → เลือก "ระบุช่วงเวลา"',
          desc: 'คลิกวันที่ที่ต้องการ เลือกประเภท "ระบุช่วงเวลา" จากนั้นเลือก slot ที่ต้องการปิด กรอกหมายเหตุ แล้วกด "บันทึกการตั้งค่า"',
          bullets: [
            'สามารถเลือกปิดเฉพาะบาง slot ได้',
            'ปฏิทินจะแสดงแถบสีส้มบนวันนั้น'
          ],
          img: imgHoliday02b,
          imgLabel: 'ฟอร์มเพิ่มวันหยุด — ระบุช่วงเวลา'
        },
        {
          title: 'ลบวันหยุด',
          subtitle: 'ลบผ่านตารางรายการวันหยุด',
          desc: 'ด้านล่างปฏิทินมีตารางแสดงวันหยุดทั้งหมด คลิกปุ่มถังขยะเพื่อลบ',
          img: imgHoliday03,
          imgLabel: 'ตารางรายการวันหยุด'
        }
      ],
      statuses: [
        {
          key: 'pending',
          label: 'รอรับสินค้า',
          bg: '#f97316',
          text: '#ffffff',
          meaning: 'Vendor จองและยืนยันการส่งแล้ว รอคลังดำเนินการรับ',
          desc: 'สถานะเริ่มต้นเมื่อ vendor สร้างการจองสำเร็จ',
          actions: ['ยืนยันการจัดส่ง', 'ปฏิเสธ', 'พิมพ์ GRN']
        },
        {
          key: 'delivered',
          label: 'จัดส่งสำเร็จ',
          bg: '#10b981',
          text: '#ffffff',
          meaning: 'คลังรับสินค้าเรียบร้อยแล้ว',
          desc: 'เกิดขึ้นหลังจากเจ้าหน้าที่คลังกดอนุมัติการจัดส่ง',
          actions: ['พิมพ์ GRN']
        },
        {
          key: 'noshow',
          label: 'ไม่มาจัดส่ง',
          bg: '#ef4444',
          text: '#ffffff',
          meaning: 'Vendor ไม่มาจัดส่งตามนัดหมาย',
          desc: 'เจ้าหน้าที่คลังบันทึกเมื่อ vendor ไม่มาตามเวลาที่จองไว้',
          actions: ['พิมพ์ GRN']
        },
        {
          key: 'rejected',
          label: 'ปฏิเสธโดยคลัง',
          bg: '#9f1239',
          text: '#ffffff',
          meaning: 'คลังปฏิเสธการรับสินค้าในการจองนี้',
          desc: 'เกิดขึ้นเมื่อเจ้าหน้าที่กดปฏิเสธและระบุเหตุผล เช่น เอกสารไม่ครบ',
          actions: []
        },
        {
          key: 'cancelled',
          label: 'ยกเลิกแล้ว',
          bg: '#9ca3af',
          text: '#ffffff',
          meaning: 'การจองถูกยกเลิก',
          desc: 'Vendor หรือระบบยกเลิกการจองก่อนถึงวันส่งสินค้า',
          actions: []
        }
      ],
      roles: [
        { name: 'warehouse_manager', view: true, approve: true, reject: true, settings: true },
        { name: 'warehouse', view: true, approve: true, reject: false, settings: false },
        { name: 'merchandising_planner', view: true, approve: false, reject: false, settings: false },
        { name: 'merchandiser', view: true, approve: false, reject: false, settings: false }
      ]
    }
  },
  computed: {
    isAdmin () {
      return ADMIN_ROLES.includes(getRole())
    },
    visibleTabs () {
      return this.tabs.filter((t) => !t.adminOnly || this.isAdmin)
    },
    visibleReceiveSteps () {
      return this.receiveSteps.filter((s) => !s.adminOnly || this.isAdmin)
    }
  },
  methods: {
    openLightbox (src, label) {
      if (!src) return
      this.lightboxSrc = src
      this.lightboxLabel = label || ''
      this.lightboxDialog = true
    }
  }
}

</script>

<style scoped>
.step-circle-sm {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: #e3f2fd;
  border: 2px solid #1976d2;
  color: #1565c0;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.img-placeholder {
  background-color: #fafafa;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.status-card-top {
  border-radius: 4px 4px 0 0;
}

.img-clickable {
  cursor: zoom-in;
  transition: opacity 0.15s;
}

.img-clickable:hover {
  opacity: 0.88;
}

.receive-step-num {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #e3f2fd;
  border: 2px solid #1976d2;
  color: #1565c0;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}
</style>
